import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaDollarSign, FaPlus, FaTrash, FaSave, FaUndo } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const GRADIENT_OPTIONS = [
  'from-[#3b82f6] to-[#1d4ed8]',
  'from-[#06b6d4] to-[#0891b2]',
  'from-[#f97316] to-[#ea580c]',
  'from-[#8b5cf6] to-[#7c3aed]',
  'from-[#ec4899] to-[#db2777]',
  'from-[#10b981] to-[#059669]',
];

const emptyPlan = (index) => ({
  title: '',
  priceVal: 0,
  frequency: '',
  features: [''],
  button: 'Buy Now',
  gradient: GRADIENT_OPTIONS[0],
  iconColor: 'text-[#3b82f6]',
  note: '',
  isDynamicBookkeeping: false,
  isDynamicCFO: false,
  order: index,
});

const PricingCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    hero: { title: '', subtitle: '' },
    slider: {
      maxDots: 6,
      baseBookkeepingPrice: 100,
      baseCFOPrice: 200,
      priceIncrement: 60,
      baseExpenseRange: 20000,
      expenseIncrement: 15000,
    },
    plans: [],
    faqsTitle: '',
    faqsSubtitle: '',
    faqs: [],
    isPublished: true,
  });

  const fetchPage = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/pricing/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const d = res.data.data;
        setForm({
          hero: d.hero || { title: '', subtitle: '' },
          slider: d.slider || form.slider,
          isPublished: d.isPublished !== false,
          plans: (() => {
            let list = (d.plans || []).map((p, i) => ({
              _id: p._id,
              title: p.title || '',
              priceVal: p.priceVal ?? 0,
              frequency: p.frequency || '',
              features: p.features?.length ? p.features : [''],
              button: p.button || 'Buy Now',
              gradient: p.gradient || GRADIENT_OPTIONS[0],
              iconColor: p.iconColor || 'text-[#3b82f6]',
              note: p.note || '',
              isDynamicBookkeeping: !!p.isDynamicBookkeeping,
              isDynamicCFO: !!p.isDynamicCFO,
              order: typeof p.order === 'number' ? p.order : i,
            }));
            if (!list.some(p => p.title?.toLowerCase().includes('sales tax'))) {
              list.push({
                title: 'Sales Tax Module',
                priceVal: 40,
                frequency: 'per filing',
                features: [
                  'Sales & Use Tax Calculation',
                  'Multi-State Nexus Review',
                  'State Filing Compliance',
                  'Gross Receipts & Exemption Review',
                  'Filing Confirmation & Records',
                ],
                button: 'Buy Now',
                gradient: GRADIENT_OPTIONS[0],
                iconColor: 'text-[#6366f1]',
                note: '',
                isDynamicBookkeeping: false,
                isDynamicCFO: false,
                order: list.length,
              });
            }
            return list;
          })(),
          faqsTitle: d.faqsTitle || '',
          faqsSubtitle: d.faqsSubtitle || '',
          faqs: (d.faqs || []).map((f, i) => ({
            _id: f._id,
            question: f.question || '',
            answer: f.answer || '',
            isActive: f.isActive !== false,
            order: typeof f.order === 'number' ? f.order : i,
          })),
        });
      }
    } catch (err) {
      toastError('Failed to load pricing page');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPage(); }, []);

  const updateField = (section, field, value) => {
    setForm((p) => ({ ...p, [section]: { ...p[section], [field]: value } }));
  };

  const updatePlan = (index, field, value) => {
    setForm((p) => {
      const plans = [...p.plans];
      plans[index] = { ...plans[index], [field]: value };
      return { ...p, plans };
    });
  };

  const updatePlanFeature = (planIndex, featureIndex, value) => {
    setForm((p) => {
      const plans = [...p.plans];
      const features = [...plans[planIndex].features];
      features[featureIndex] = value;
      plans[planIndex] = { ...plans[planIndex], features };
      return { ...p, plans };
    });
  };

  const addPlan = () => setForm((p) => ({ ...p, plans: [...p.plans, emptyPlan(p.plans.length)] }));
  const removePlan = (index) => {
    if (!window.confirm('Remove this plan?')) return;
    setForm((p) => ({ ...p, plans: p.plans.filter((_, i) => i !== index) }));
  };

  const addFAQ = () => {
    setForm((p) => ({
      ...p,
      faqs: [...p.faqs, { question: '', answer: '', isActive: true, order: p.faqs.length }],
    }));
  };

  const removeFAQ = (index) => {
    if (!window.confirm('Remove this FAQ?')) return;
    setForm((p) => ({
      ...p,
      faqs: p.faqs.filter((_, i) => i !== index),
    }));
  };

  const updateFAQField = (index, field, value) => {
    setForm((p) => {
      const faqs = [...p.faqs];
      faqs[index] = { ...faqs[index], [field]: value };
      return { ...p, faqs };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/pricing/admin`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Pricing page saved');
        fetchPage();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore default pricing content?')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/pricing/admin/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Defaults restored');
        fetchPage();
      }
    } catch (err) {
      toastError('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Pricing Page CMS"
        description="Edit pricing hero, slider settings, and plan cards"
        icon={FaDollarSign}
        actions={
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleReset} disabled={saving || loading} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium flex items-center gap-2">
              <FaUndo /> Reload Defaults
            </button>
            <button onClick={handleSave} disabled={saving || loading} className="btn-premium flex items-center gap-2">
              <FaSave /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      {loading ? (
        <SkeletonLoader variant="page" />
      ) : (
        <div className="space-y-6">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Hero</h2>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.isPublished} onChange={(e) => setForm((p) => ({ ...p, isPublished: e.target.checked }))} />
                Published
              </label>
            </div>
            <input className="input-modern" placeholder="Title" value={form.hero.title} onChange={(e) => updateField('hero', 'title', e.target.value)} />
            <textarea className="input-modern min-h-[70px]" placeholder="Subtitle" value={form.hero.subtitle} onChange={(e) => updateField('hero', 'subtitle', e.target.value)} />
          </div>

          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
            <h2 className="text-lg font-bold">Slider Settings</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(form.slider).map(([key, val]) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs uppercase text-gray-500">{key}</label>
                  <input className="input-modern text-sm" type="number" value={val} onChange={(e) => updateField('slider', key, Number(e.target.value))} />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Pricing Plans</h2>
              <button onClick={addPlan} className="text-sm text-primary-600 flex items-center gap-1"><FaPlus /> Add Plan</button>
            </div>
            {form.plans.map((plan, pIndex) => (
              <div key={plan._id || pIndex} className="rounded-xl border border-gray-200 dark:border-dark-600 p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-sm">Plan #{pIndex + 1}</span>
                  <button onClick={() => removePlan(pIndex)} className="text-red-400"><FaTrash /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input className="input-modern text-sm" placeholder="Title" value={plan.title} onChange={(e) => updatePlan(pIndex, 'title', e.target.value)} />
                  <input className="input-modern text-sm" type="number" placeholder="Price" value={plan.priceVal} onChange={(e) => updatePlan(pIndex, 'priceVal', Number(e.target.value))} />
                  <input className="input-modern text-sm" placeholder="Frequency" value={plan.frequency} onChange={(e) => updatePlan(pIndex, 'frequency', e.target.value)} />
                  <select className="input-modern text-sm" value={plan.gradient} onChange={(e) => updatePlan(pIndex, 'gradient', e.target.value)}>
                    {GRADIENT_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <input className="input-modern text-sm" placeholder="Icon color class" value={plan.iconColor} onChange={(e) => updatePlan(pIndex, 'iconColor', e.target.value)} />
                  <input className="input-modern text-sm" placeholder="Note (optional)" value={plan.note} onChange={(e) => updatePlan(pIndex, 'note', e.target.value)} />
                </div>
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={plan.isDynamicBookkeeping} onChange={(e) => updatePlan(pIndex, 'isDynamicBookkeeping', e.target.checked)} /> Dynamic Bookkeeping</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={plan.isDynamicCFO} onChange={(e) => updatePlan(pIndex, 'isDynamicCFO', e.target.checked)} /> Dynamic CFO</label>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase text-gray-500">Features</p>
                  {plan.features.map((feature, fIndex) => (
                    <input key={fIndex} className="input-modern text-sm" value={feature} onChange={(e) => updatePlanFeature(pIndex, fIndex, e.target.value)} />
                  ))}
                  <button onClick={() => updatePlan(pIndex, 'features', [...plan.features, ''])} className="text-xs text-primary-600">+ Add feature</button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold">Pricing FAQs</h2>
              <button
                type="button"
                onClick={addFAQ}
                className="text-sm text-primary-600 flex items-center gap-1 font-semibold"
              >
                <FaPlus /> Add FAQ
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Section Title</label>
                <input
                  className="input-modern text-sm"
                  placeholder="Pricing FAQs"
                  value={form.faqsTitle}
                  onChange={(e) => setForm(p => ({ ...p, faqsTitle: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Section Subtitle</label>
                <input
                  className="input-modern text-sm"
                  placeholder="Frequently Asked Questions"
                  value={form.faqsSubtitle}
                  onChange={(e) => setForm(p => ({ ...p, faqsSubtitle: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-4 pt-2">
              {form.faqs?.map((faq, idx) => (
                <div key={faq._id || idx} className="rounded-xl border border-gray-200 dark:border-dark-600 p-4 space-y-3 bg-gray-50/20 dark:bg-dark-900/5 relative">
                  <button
                    type="button"
                    onClick={() => removeFAQ(idx)}
                    className="absolute top-4 right-4 text-red-500 border border-red-200 dark:border-dark-600 rounded-lg p-1.5 hover:bg-red-50 dark:hover:bg-red-950/20"
                  >
                    <FaTrash className="w-3.5 h-3.5" />
                  </button>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-sm">FAQ Item #{idx + 1}</span>
                    <label className="flex items-center gap-2 text-xs mr-10 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={faq.isActive}
                        onChange={(e) => updateFAQField(idx, 'isActive', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Active
                    </label>
                  </div>
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Question</label>
                      <input
                        className="input-modern text-sm"
                        placeholder="Question text"
                        value={faq.question}
                        onChange={(e) => updateFAQField(idx, 'question', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Answer</label>
                      <textarea
                        className="input-modern text-sm min-h-[60px]"
                        placeholder="Answer text"
                        value={faq.answer}
                        onChange={(e) => updateFAQField(idx, 'answer', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default PricingCMS;
